/**
 * Settings — preferences, measured latency, storage management, and the capability matrix.
 *
 * The matrix is generated by probing this browser, not written by hand. A row that says
 * "unavailable" is the result of a real feature test that just ran, which is the only kind
 * of capability claim worth showing anyone.
 */

import { h, onCleanup } from '../../core/dom';
import { computed, effect, signal } from '../../core/signal';
import { icon, neonButton, segmentedRail, toggle } from '../components/controls';
import { confirmDialog } from '../components/sheet';
import { probeCapabilities, renderCapabilityTable, capabilityMarkdown, type CapabilityReport } from '../../platform/capabilities';
import type { ProjectSnapshot } from '../../storage/project-store';
import { isStandalone, displayMode } from '../../platform/standalone';
import { screenWakeLockSupported } from '../../platform/wake-lock';
import { copyText, downloadFile, installationURL } from '../../platform/share';
import { renderQRCanvas } from '../../platform/install';
import { hapticsAvailable, hasVibrationAPI } from '../../platform/haptics';
import {
  engine,
  engineLatencySummary,
  notify,
  project,
  projectStore,
  projectVersions,
  restoreProjectVersion,
  saveNow,
  saveProjectVersion,
  projects,
  recorder,
  refreshStorageReport,
  reportFailure,
  settings,
  storageReport,
  TEXT_SCALES,
  updateSettings,
} from '../../app/state';
import { navigate } from '../../app/router';
import { callout, formatBytes, presentPrompt, screenBody, screenHeader, section } from '../../app/shell';



function latencyPanel(): HTMLElement {
  const body = h('div', { class: 'latency-panel' });
  const refresh = (): void => {
    const summary = engineLatencySummary();
    const reported = engine.outputLatencyMs.peek();
    body.replaceChildren(
      h(
        'div',
        { class: 'latency-grid' },
        latencyCell('Median', summary.samples === 0 ? 'Not yet' : `${summary.median.toFixed(1)} ms`),
        latencyCell('p95', summary.samples === 0 ? 'Not yet' : `${summary.p95.toFixed(1)} ms`),
        latencyCell('Worst', summary.samples === 0 ? 'Not yet' : `${summary.worst.toFixed(1)} ms`),
        latencyCell('Samples', String(summary.samples)),
      ),
      // Dropouts and failed starts, counted rather than assumed absent. Nothing in the app
      // counted either before; a latency figure with no glitch count beside it is only half
      // an answer, because a fast average and a stuttering output feel identical on paper.
      h(
        'div',
        { class: 'latency-grid' },
        latencyCell('Dropouts', String(engine.glitches.peek())),
        latencyCell('Failed starts', String(engine.failedStarts.peek())),
        latencyCell('Route changes', String(engine.routeChanged.peek())),
      ),
      h(
        'p',
        { class: 'muted' },
        summary.samples === 0
          ? 'Play some pads and come back. These numbers are measured from real triggers, never estimated.'
          : reported >= 0
            ? `Each figure is the whole path: the browser's own event delivery, PadLab's scheduling, and the ${reported.toFixed(1)} ms of output latency this browser reports.`
            : 'This browser will not report its output latency, so these figures cover event delivery and scheduling only. The true figure at the speaker is higher.',
      ),
      h(
        'p',
        { class: 'muted' },
        `Sample rate ${engine.sampleRate.peek() === 0 ? 'not started' : `${engine.sampleRate.peek()} Hz`}. Bluetooth output adds 100–200 ms that no app can remove.`,
      ),
    );
  };
  refresh();
  const timer = setInterval(refresh, 2000);
  onCleanup(body, () => clearInterval(timer));
  return body;
}

function latencyCell(title: string, value: string): HTMLElement {
  return h('div', { class: 'latency-cell' }, h('p', { class: 'kicker' }, title), h('p', { class: 'numeric latency-value' }, value));
}

/**
 * Version history, and the explicit save that makes one.
 *
 * `snapshots()`, `takeSnapshot()` and `restoreSnapshot()` were complete and had no caller:
 * versions were being written on every launch and were reachable only as an internal
 * corruption fallback, so "version history or recoverable snapshots" existed everywhere
 * except in front of the person it was for. `saveNow()` likewise had zero callers — autosave
 * was the only route to disk, and an instrument that never says "saved" is an instrument
 * people do not trust.
 */
function versionPanel(): HTMLElement {
  const body = h('div', { class: 'version-panel' });
  const versions = signal<readonly ProjectSnapshot[]>([]);
  const busy = signal(false);

  const refresh = (): void => {
    void projectVersions()
      .then((list) => {
        versions.value = list;
      })
      .catch(() => {
        versions.value = [];
      });
  };
  refresh();

  const stop = effect(() => {
    const list = versions.value;
    body.replaceChildren();
    if (project.value === null) {
      body.appendChild(h('p', { class: 'muted' }, 'Open a project to see its versions.'));
      return;
    }
    if (list.length === 0) {
      body.appendChild(h('p', { class: 'muted' }, 'No versions yet. Save one before a change you are unsure about.'));
      return;
    }
    for (const version of list.slice(0, 12)) {
      body.appendChild(
        h(
          'div',
          { class: 'version-row' },
          h(
            'span',
            { class: 'version-titles' },
            h('span', { class: 'version-name' }, version.label ?? 'Version'),
            h(
              'span',
              { class: 'version-meta' },
              `${new Date(version.createdAt).toLocaleString()} · ${formatBytes(version.byteSize)}${version.isAutomatic ? ' · automatic' : ''}`,
            ),
          ),
          neonButton({
            title: 'Restore',
            style: 'secondary',
            onPress: () => {
              confirmDialog({
                title: 'Restore this version?',
                message:
                  'The board you have now is kept as a version too, so this can be undone by restoring the other way.',
                confirmTitle: 'Restore',
                destructive: false,
                onConfirm: () => {
                  void restoreProjectVersion(version.id)
                    .then(() => {
                      notify('Restored', version.label ?? 'Version');
                      refresh();
                    })
                    .catch((error: unknown) => reportFailure('That version could not be restored', error));
                },
              }).present();
            },
          }).el,
        ),
      );
    }
  });
  onCleanup(body, stop);

  return h(
    'div',
    {},
    h(
      'div',
      { class: 'export-actions' },
      neonButton({
        title: 'Save now',
        style: 'primary',
        enabled: computed(() => !busy.value && project.value !== null),
        onPress: () => {
          busy.value = true;
          void saveNow()
            .then(() => notify('Saved', 'Everything is on this device.'))
            .catch((error: unknown) => reportFailure('Save failed', error))
            .finally(() => {
              busy.value = false;
            });
        },
      }).el,
      neonButton({
        title: 'Save a version',
        style: 'secondary',
        enabled: computed(() => !busy.value && project.value !== null),
        onPress: () =>
          presentPrompt({
            title: 'Name this version',
            value: `Version ${new Date().toLocaleDateString()}`,
            onConfirm: (label) => {
              busy.value = true;
              void saveProjectVersion(label)
                .then(() => {
                  notify('Version saved', label);
                  refresh();
                })
                .catch((error: unknown) => reportFailure('That version could not be saved', error))
                .finally(() => {
                  busy.value = false;
                });
            },
          }),
      }).el,
    ),
    body,
  );
}

function storagePanel(): HTMLElement {
  const body = h('div', { class: 'storage-panel' });
  const stop = effect(() => {
    const report = storageReport.value;
    body.replaceChildren();
    if (report === null) {
      body.appendChild(h('p', { class: 'muted' }, 'Measuring…'));
      return;
    }
    body.append(
      row('Recordings and imports', formatBytes(report.mediaBytes)),
      row('Projects', formatBytes(report.projectsBytes)),
      row('Waveform caches', formatBytes(report.peaksBytes)),
      row('Rendered previews', formatBytes(report.rendersBytes)),
      row('In-progress recordings', formatBytes(report.recordingsBytes)),
      row('Downloaded packs', formatBytes(report.downloadedPacksBytes)),
      row('Free space the browser reports', report.availableBytes > 0 ? formatBytes(report.availableBytes) : 'Not reported'),
      neonButton({
        title: `Clear caches (${formatBytes(report.reclaimableBytes)})`,
        style: 'secondary',
        onPress: () => {
          confirmDialog({
            title: 'Clear regenerable caches?',
            message:
              'This removes waveform pictures and rendered previews only. Your recordings, imports and projects are never touched. Anything cleared is rebuilt the next time you need it.',
            confirmTitle: 'Clear caches',
            onConfirm: () => {
              void projectStore()
                .clearRegenerableCaches()
                .then(async (freed) => {
                  notify('Caches cleared', formatBytes(freed));
                  await refreshStorageReport();
                })
                .catch((error: unknown) => reportFailure('Nothing could be cleared', error));
            },
          }).present();
        },
      }).el,
    );
  });
  onCleanup(body, stop);
  void refreshStorageReport();
  return body;
}

function row(title: string, value: string): HTMLElement {
  return h('div', { class: 'kv-row' }, h('span', null, title), h('span', { class: 'numeric' }, value));
}

function installPanel(): HTMLElement {
  const url = installationURL();
  return h(
    'div',
    { class: 'install-guide' },
    h('p', { class: 'muted' }, `Display mode: ${displayMode.peek()}`),
    isStandalone.peek()
      ? callout('Installed', 'PadLab is running as an app', 'Full-screen, offline-capable, and separate from your browser tabs.', 'accent')
      : callout(
          'Add to Home Screen',
          'Three taps in Safari',
          '1. Tap the Share button at the bottom of Safari. 2. Scroll to “Add to Home Screen”. 3. Tap Add. PadLab then launches full-screen with no browser chrome and opens with no connection.',
          'accent',
        ),
    h(
      'div',
      { class: 'install-actions' },
      neonButton({
        title: 'Copy the install link',
        style: 'secondary',
        onPress: () => {
          void copyText(url).then((ok) => {
            if (ok) notify('Link copied', url);
            else reportFailure('Copy failed', new Error('Your browser blocked clipboard access. Select the address bar and copy it manually.'));
          });
        },
      }).el,
    ),
    // The shareable installation URL *and* its QR code. The encoder has been in the tree the
    // whole time in a module nothing imported, so "a shareable installation URL and QR code"
    // shipped as a URL and a copy button. Handing someone a phone to point at a screen is the
    // whole reason a QR code is asked for here.
    qrPanel(url),
    h('p', { class: 'kicker' }, url),
  );
}

function qrPanel(url: string): HTMLElement {
  const host = h('div', { class: 'install-qr' });
  try {
    const canvas = renderQRCanvas(url, { size: 176 });
    canvas.setAttribute('role', 'img');
    canvas.setAttribute('aria-label', `QR code for ${url}`);
    host.append(canvas, h('p', { class: 'muted' }, 'Point another phone\u2019s camera at this to open PadLab on it.'));
  } catch {
    // An encoder failure must not take the rest of the panel with it; the link above still
    // does the job.
    host.append(h('p', { class: 'muted' }, 'The QR code could not be drawn in this browser. Use the link below.'));
  }
  return host;
}

export function settingsScreen(): HTMLElement {
  const report = signal<CapabilityReport | null>(null);
  const matrixHost = h('div', { class: 'capability-host' }, h('p', { class: 'muted' }, 'Probing this browser…'));

  void (async (): Promise<void> => {
    try {
      const probed = await probeCapabilities();
      report.value = probed;
      matrixHost.replaceChildren(renderCapabilityTable(probed));
    } catch (error) {
      matrixHost.replaceChildren(h('p', { class: 'muted' }, 'The capability probe could not finish in this browser.'));
      reportFailure('Capability probe failed', error);
    }
  })();

  const micState = computed(() => recorder.permission.value);

  const screen = h(
    'div',
    { class: 'screen screen--settings' },
    screenHeader({ kicker: 'PadLab', title: 'Settings' }),
    screenBody(
      // First, not last. The tour used to live at the bottom of About — underneath the whole
      // capability report — which put roughly fifty rows of diagnostics between a lost user
      // and the one control that explains the app. Anyone who needs it needs it immediately.
      section(
        'Tour',
        neonButton({
          title: 'Replay the tour',
          style: 'secondary',
          onPress: () => void navigate({ name: 'onboarding' }),
        }).el,
        h('p', { class: 'muted' }, 'Walks through pads, recording and beats again. It never turns on your microphone.'),
      ),
      section(
        'Text size',
        segmentedRail({
          items: TEXT_SCALES.map((scale) => ({ id: String(scale), title: `${Math.round(scale * 100)}%` })),
          selection: computed(() => String(settings.value.textScale)),
          label: 'Text size',
          onSelect: (id) => updateSettings({ textScale: Number.parseFloat(id) }),
        }).el,
        h(
          'p',
          { class: 'muted' },
          'Scales every label in PadLab. Your device’s own text-size setting applies on top of this. Pad labels stop growing sooner than the rest so the grid stays playable.',
        ),
      ),
      section(
        'Feel',
        // Offered only where it can actually do something. A switch that is present but inert
        // is worse than an absent one: it tells the user the feature exists and that they have
        // turned it on, and then nothing happens, which reads as a broken app rather than a
        // platform without the capability. What the browser can and cannot do is reported once,
        // honestly, in the capability matrix further down.
        ...(hapticsAvailable()
          ? [
              toggle({
                title: 'Haptics',
                subtitle: hasVibrationAPI()
                  ? 'A short buzz on every pad hit.'
                  : 'A light tap on every pad hit. iOS gives web apps one fixed tap, so it cannot follow how hard you play. The native PadLab uses the full Taptic Engine.',
                on: computed(() => settings.value.hapticsEnabled),
                onChange: (on) => updateSettings({ hapticsEnabled: on }),
              }).el,
            ]
          : []),
        toggle({
          title: 'Keep the screen awake while performing',
          subtitle: screenWakeLockSupported
            ? 'Holds a wake lock on the board so the display does not dim mid-set.'
            : 'This browser has no Screen Wake Lock API, so the display follows your usual timeout.',
          on: computed(() => settings.value.wakeLockEnabled),
          enabled: screenWakeLockSupported,
          onChange: (on) => updateSettings({ wakeLockEnabled: on }),
        }).el,
        toggle({
          title: 'Advanced mode',
          subtitle: 'Shows every parameter in the Sound Lab and the full effect rack instead of the simple controls.',
          on: computed(() => settings.value.advancedMode),
          onChange: (on) => updateSettings({ advancedMode: on }),
        }).el,
      ),
      section(
        'Recording',
        toggle({
          title: 'Monitor by default',
          subtitle: 'Off is the safe default: monitoring through the phone speaker feeds back. PadLab asks you to confirm headphones before it turns on either way.',
          on: computed(() => settings.value.monitoringDefault),
          onChange: (on) => updateSettings({ monitoringDefault: on }),
        }).el,
        toggle({
          title: 'Count-in before recording',
          subtitle: 'One bar of clicks so you can come in on the beat.',
          on: computed(() => settings.value.countInEnabled),
          onChange: (on) => updateSettings({ countInEnabled: on }),
        }).el,
        h(
          'p',
          { class: 'muted' },
          computed(() => {
            switch (micState.value) {
              case 'granted':
                return 'Microphone access is granted. PadLab opens the microphone only while you are recording and releases it when you leave the Voice Lab.';
              case 'denied':
                return 'Microphone access is blocked. In Safari: aA in the address bar → Website Settings → Microphone → Allow. In an installed app: Settings → PadLab → Microphone.';
              case 'unsupported':
                return 'This browser exposes no microphone API, so recording is unavailable. You can still import sounds.';
              default:
                return 'PadLab has not asked for the microphone yet. It asks in the Voice Lab, when you press the button that says so.';
            }
          }),
        ),
      ),
      section('Versions', versionPanel()),
      section('Measured latency', latencyPanel()),
      section('Storage', storagePanel()),
      section(
        'Your data',
        h('p', { class: 'muted' }, 'Everything is stored on this device. Nothing is uploaded unless you share it yourself.'),
        neonButton({
          title: 'Export every project',
          style: 'secondary',
          onPress: () => {
            void (async (): Promise<void> => {
              try {
                for (const summary of projects.peek()) {
                  const blob = await projectStore().exportPackage(summary.id);
                  downloadFile(blob, projectStore().packageFileName(summary.name));
                }
                notify('Exported', `${projects.peek().length} project backups.`);
              } catch (error) {
                reportFailure('Export failed', error);
              }
            })();
          },
        }).el,
        neonButton({
          title: 'Copy the capability report',
          style: 'ghost',
          onPress: () => {
            const current = report.peek();
            if (current === null) {
              notify('Still probing', 'Give it a moment and try again.');
              return;
            }
            void copyText(capabilityMarkdown(current)).then((ok) => {
              if (ok) notify('Copied', 'Paste it into a bug report.');
            });
          },
        }).el,
      ),
      section('Install', installPanel()),
      section('What this browser can do', matrixHost),
      section(
        'About',
        h(
          'div',
          { class: 'about-rows' },
          row('Open project', project.peek()?.name ?? 'None'),
          row('Projects on this device', String(projects.peek().length)),
          row('Audio', engine.isUnlocked.peek() ? 'Running' : 'Waiting for a touch'),
        ),
        h(
          'p',
          { class: 'kicker about-note' },
          'PadLab is dark by design on both platforms. A performance surface should not change contrast when you walk outdoors.',
        ),
        h('p', { class: 'muted' }, icon('info.circle.fill', { size: 14 }), ' Project files are byte-compatible with the iPhone app.'),
      ),
    ),
  );
  return screen;
}
